// =============================================================================
// /admin/printing — centralny organ druku w powłoce (plan §7): pięć zakładek
// nad wspólnym nagłówkiem. Druk to kartoteka infrastrukturalna organizacji,
// nie moduł biznesowy — stąd strona natywna powłoki obok SitesPage/DevicesPage.
//
// Zakładka i filtr lokalizacji żyją w URL (`?tab=`, `?site=`), żeby dało się
// podesłać komuś link „zobacz w Kolejce" i żeby odświeżenie strony nie gubiło
// kontekstu. Filtr lokalizacji jest PRZYGOTOWANIEM pod Fazę 7 (metadane
// `site_id` na kartotekach) — przy 0–1 lokalizacjach w ogóle się nie pokazuje,
// bo filtrowanie po jedynej lokalizacji to szum.
// =============================================================================

import { Alert, Select, Tabs, TabsContent, TabsList, TabsTrigger } from '@insolitum/ui';
import { FileText, ListTodo, Printer, Route, Server } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AgentsTab } from './AgentsTab';
import { AssignmentsTab } from './AssignmentsTab';
import { JobsTab } from './JobsTab';
import { PrintersTab } from './PrintersTab';
import { TemplatesTab } from './TemplatesTab';
import { czySerwerDrukuOffline } from './status';
import { useAgents, useSites } from './usePrintingData';

const ZAKLADKI = ['printers', 'agents', 'jobs', 'templates', 'assignments'] as const;
type Zakladka = (typeof ZAKLADKI)[number];

function czytajZakladke(surowa: string | null): Zakladka {
  return (ZAKLADKI as readonly string[]).includes(surowa ?? '') ? (surowa as Zakladka) : 'printers';
}

export function PrintingPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const zakladka = czytajZakladke(searchParams.get('tab'));
  const siteId = searchParams.get('site');

  const { lokalizacje } = useSites();
  // Agenci na poziomie strony służą WYŁĄCZNIE bannerowi zdrowia — zakładka
  // Serwery druku ma własny odczyt z odświeżaniem po akcjach.
  const { agenci } = useAgents();
  const offline = czySerwerDrukuOffline(agenci);

  const ustawParam = (klucz: string, wartosc: string | null) => {
    const nastepne = new URLSearchParams(searchParams);
    if (wartosc) nastepne.set(klucz, wartosc);
    else nastepne.delete(klucz);
    setSearchParams(nastepne, { replace: true });
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Nagłówek — konwencja pagehead jak SitesPage/DevicesPage */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Printer className="w-6 h-6 text-aurora-brand" />
            {t('printing.title', 'Drukowanie')}
          </h1>
          <p className="pagehead__sub">
            {t(
              'printing.subtitle',
              'Centralne zarządzanie drukarkami, serwerami wydruku, kolejką i szablonami'
            )}
          </p>
        </div>
        {/* Filtr lokalizacji: ukryty przy 0–1 lokalizacjach (Faza 7). */}
        {lokalizacje.length > 1 && (
          <div className="pagehead__actions w-56">
            <Select
              aria-label={t('printing.siteFilter', 'Lokalizacja')}
              value={siteId ?? ''}
              onChange={(e) => ustawParam('site', e.target.value || null)}
              options={[
                { value: '', label: t('printing.allSites', 'Wszystkie lokalizacje') },
                ...lokalizacje.map((s) => ({ value: s.id, label: s.name })),
              ]}
            />
          </div>
        )}
      </div>

      {/* Banner zdrowia: pochodny WYŁĄCZNIE od heartbeatów agentów (próg 180 s).
          Pokazuje się nad każdą zakładką, bo martwy serwer dotyczy ich wszystkich. */}
      {offline && (
        <Alert variant="warning" className="mb-4">
          {t(
            'printing.serverOffline',
            'Serwer druku offline — zadania będą czekać w kolejce, aż serwer wróci.'
          )}
        </Alert>
      )}

      <Tabs value={zakladka} onValueChange={(v) => ustawParam('tab', v)}>
        <TabsList className="overflow-x-auto">
          <TabsTrigger value="printers" icon={<Printer className="w-4 h-4" />}>
            {t('printing.tabPrinters', 'Drukarki')}
          </TabsTrigger>
          <TabsTrigger value="agents" icon={<Server className="w-4 h-4" />}>
            {t('printing.tabAgents', 'Serwery druku')}
          </TabsTrigger>
          <TabsTrigger value="jobs" icon={<ListTodo className="w-4 h-4" />}>
            {t('printing.tabJobs', 'Kolejka')}
          </TabsTrigger>
          <TabsTrigger value="templates" icon={<FileText className="w-4 h-4" />}>
            {t('printing.tabTemplates', 'Szablony')}
          </TabsTrigger>
          <TabsTrigger value="assignments" icon={<Route className="w-4 h-4" />}>
            {t('printing.tabAssignments', 'Przypisania')}
          </TabsTrigger>
        </TabsList>

        {/* TabsContent renderuje tylko aktywną zakładkę — nieaktywne nie
            odpytują bazy ani print-core. */}
        <TabsContent value="printers">
          <PrintersTab siteId={siteId} />
        </TabsContent>
        <TabsContent value="agents">
          <AgentsTab siteId={siteId} />
        </TabsContent>
        <TabsContent value="jobs">
          <JobsTab siteId={siteId} />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="assignments">
          <AssignmentsTab siteId={siteId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
